/**
 * Manipulation directe du <head> pour le SEO — CRA n'a pas de rendu serveur,
 * donc pas de react-helmet ici : on met à jour les balises existantes de
 * public/index.html à chaque navigation au lieu d'en empiler de nouvelles.
 */

export const SITE_URL = "https://rambanews.com";
export const SITE_NAME = "Ramba News";

/** Coupe une description au mot le plus proche avant la limite (~160 car. pour Google). */
export function truncate(text: string, max = 160): string {
  const clean = text.replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  const cut = clean.slice(0, max);
  return `${cut.slice(0, cut.lastIndexOf(" "))}…`;
}

function upsertTag(selector: string, create: () => HTMLElement, attr: string, value: string) {
  let el = document.head.querySelector(selector) as HTMLElement | null;
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
}

export function setMeta(name: string, content: string) {
  upsertTag(`meta[name="${name}"]`, () => {
    const m = document.createElement("meta");
    m.setAttribute("name", name);
    return m;
  }, "content", content);
}

export function setMetaProperty(property: string, content: string) {
  upsertTag(`meta[property="${property}"]`, () => {
    const m = document.createElement("meta");
    m.setAttribute("property", property);
    return m;
  }, "content", content);
}

export function setCanonical(path: string) {
  const href = path.startsWith("http") ? path : `${SITE_URL}${path}`;
  upsertTag('link[rel="canonical"]', () => {
    const l = document.createElement("link");
    l.setAttribute("rel", "canonical");
    return l;
  }, "href", href);
  return href;
}

export interface SeoOptions {
  title: string;
  description: string;
  path: string;
  image?: string;
  type?: "website" | "article";
  /** Pages à faible valeur (résultats de recherche filtrés, etc.) : crawlables mais pas indexées. */
  noindex?: boolean;
}

/** Titre, description, canonical, Open Graph et Twitter Card — appelée une fois par page. */
export function applySeo({ title, description, path, image, type = "website", noindex = false }: SeoOptions) {
  document.title = title;
  setMeta("description", description);
  setMeta("robots", noindex ? "noindex, follow" : "index, follow");

  const url = setCanonical(path);
  const img = image ?? `${SITE_URL}/logo_Ramba.png`;

  setMetaProperty("og:type", type);
  setMetaProperty("og:site_name", SITE_NAME);
  setMetaProperty("og:title", title);
  setMetaProperty("og:description", description);
  setMetaProperty("og:url", url);
  setMetaProperty("og:image", img);

  setMeta("twitter:card", "summary_large_image");
  setMeta("twitter:title", title);
  setMeta("twitter:description", description);
  setMeta("twitter:image", img);
}

/** Injecte/met à jour un bloc JSON-LD identifié par id ; le supprimer si data est null. */
export function setJsonLd(id: string, data: unknown | null) {
  const existing = document.getElementById(id);
  if (data === null) {
    existing?.remove();
    return;
  }
  const script = (existing as HTMLScriptElement | null) ?? document.createElement("script");
  script.id = id;
  script.type = "application/ld+json";
  script.textContent = JSON.stringify(data);
  if (!existing) document.head.appendChild(script);
}
